// GET /api/erp/me — who is signed in, in which business, with which role,
// capabilities and operating mode. erp-core (internal/me) is the source of
// truth for this shape; the web adapts to it.
//
// Wire contract (erp-core internal/me.Response, snake_case JSON):
// {
//   "user":         { "id": "…", "name": "سُهى", "phone": "+9647…" },
//   "business":     { "id": "…", "name": "عيادة د. سارة الحسيني" },
//   "branch":       { "id": "…", "name": "الفرع الرئيسي" },
//   "role":         "owner" | "manager" | "staff",
//   "capabilities": { "accounts.cashier": true, "accounts.manual_entry": false, … },   // all 6 keys
//   "mode":         "cloud" | "node",
//   "node":         null (cloud) | { "id", "licensed", "reason"|null, "licensee"|null, "expires_at"|null, "modules": [] },
//   "flags":        null (cloud — the web reads bff-business GET /v1/config)
//                   | { "erp.core": true, … } (node — from the licence; {} while unlicensed)
// }
// /me carries NO memberships and NO sector. The web composes them from their
// real sources (fetchMe below):
//   - sector      → GET /api/erp/settings `sector` (the ERP business setting that
//                   also picks the chart-of-accounts template; both modes).
//   - memberships → node: the login/unlock response (kept in the session store);
//                   cloud: ihjizli-profile GET /v1/business/memberships (the same
//                   source erp-core's cloud provider and bff-business rbac read),
//                   reached same-origin as /api/profile/*.

import { useQuery } from '@tanstack/react-query';
import { erpApi, isApiError, profileApi } from './api';
import { sessionStore } from './auth/session';
import type { ErpMode } from './env';

export type Role = 'staff' | 'manager' | 'owner';

/** owner(3) > manager(2) > staff(1) — mirrors erp-core rbac. */
export const ROLE_RANK: Record<Role, number> = { staff: 1, manager: 2, owner: 3 };

export function roleAtLeast(role: Role | null | undefined, min: Role): boolean {
  if (!role) return false;
  return (ROLE_RANK[role] ?? 0) >= ROLE_RANK[min];
}

export function parseRole(raw: unknown): Role | null {
  return raw === 'owner' || raw === 'manager' || raw === 'staff' ? raw : null;
}

/** Capability keys the server knows (erp-core internal/auth/capabilities.Keys). */
export type Capability =
  | 'accounts.cashier'
  | 'accounts.manual_entry'
  | 'accounts.export'
  | 'inventory.write'
  | 'team.read'
  | 'tasks.assign';

export interface Membership {
  business_id: string;
  business_name: string;
  role: Role;
}

/** erp-core internal/me.Node — present only when erp-core runs as a node. */
export interface MeNode {
  id: string;
  licensed: boolean;
  /** Why the node is not licensed ('missing' | 'expired' | …); null when licensed. */
  reason: string | null;
  licensee: string | null;
  expires_at: string | null;
  modules: string[];
}

/** The raw GET /v1/erp/me body exactly as erp-core sends it. */
export interface MeResponse {
  user: { id: string; name: string; phone: string };
  business: { id: string; name: string };
  /** `city` is profile's (the mirror appends it; absent until erp-core 2A-23 ships it on /me). */
  branch: { id: string; name: string; city?: string };
  role: string;
  capabilities: Record<string, boolean> | null;
  mode: 'cloud' | 'node';
  node: MeNode | null;
  flags: Record<string, boolean> | null;
}

/** What the app reads (useErp().me): the /me body, normalised and composed. */
export interface Me {
  user: { id: string; name: string; phone?: string };
  business: {
    id: string;
    name: string;
    /** From GET /v1/erp/settings (null when that read is not allowed, e.g. an unlicensed node). */
    sector?: string | null;
    /** Not sent by erp-core; absent unless a caller supplies it (tests). Readers must treat it as unknown. */
    country?: string;
    /** Not sent by erp-core; absent unless a caller supplies it (tests). */
    slug?: string;
  };
  /** The active branch; `city` when the server sends it (a hotel's chip reads «النشاط › المدينة»). */
  branch?: { id: string; name: string; city?: string } | null;
  role: Role;
  /** Enabled capability keys (the server's {key: bool} map, true entries only). */
  capabilities: string[];
  /** Every business this person can switch to (≥ the active one). */
  memberships: Membership[];
  mode: 'cloud' | 'node';
  node: MeNode | null;
  /** Node mode: flags from the licence. Cloud: null (flags come from GET /v1/config). */
  flags?: Record<string, boolean> | null;
}

/** Server mode → web mode ('cloud' is the server's name for online). */
export function webMode(me: Pick<Me, 'mode'> | null | undefined, fallback: ErpMode): ErpMode {
  if (!me) return fallback;
  return me.mode === 'node' ? 'node' : 'online';
}

export function hasCapability(me: Pick<Me, 'capabilities' | 'role'> | null | undefined, cap: Capability | string): boolean {
  if (!me) return false;
  if (me.role === 'owner') return true; // owner holds every switch (HANDOFF §9); erp-core sends them all true anyway
  return me.capabilities.includes(cap);
}

/** {key: bool} (erp-core) → enabled keys. Tolerates an array of keys too. */
export function enabledCapabilities(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((k): k is string => typeof k === 'string');
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw as Record<string, unknown>)
    .filter(([, v]) => v === true)
    .map(([k]) => k)
    .sort();
}

/** Only literal `true`s survive (fail closed). null stays null (cloud). */
function trueFlags(raw: unknown): Record<string, boolean> | null {
  if (raw == null || typeof raw !== 'object') return null;
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (v === true) out[k] = true;
  return out;
}

/** Normalise the /me body. Pure — memberships and sector are supplied by the caller. */
export function normalizeMe(raw: MeResponse, extra: { sector?: string | null; memberships?: Membership[] } = {}): Me {
  const role = parseRole(raw.role);
  if (!role) throw new Error(`me: unknown role ${String(raw.role)}`);
  const mode = raw.mode === 'node' ? 'node' : 'cloud';
  const self: Membership = { business_id: raw.business.id, business_name: raw.business.name, role };
  const others = (extra.memberships ?? []).filter((m) => m.business_id !== raw.business.id);
  return {
    user: { id: raw.user.id, name: raw.user.name, phone: raw.user.phone || undefined },
    business: { id: raw.business.id, name: raw.business.name, sector: extra.sector ?? null },
    branch: raw.branch?.id ? { id: raw.branch.id, name: raw.branch.name, ...(raw.branch.city ? { city: raw.branch.city } : {}) } : null,
    role,
    capabilities: enabledCapabilities(raw.capabilities),
    // The active business always appears with the role /me resolved for it.
    memberships: [self, ...others],
    mode,
    node: mode === 'node' && raw.node ? { ...raw.node, modules: raw.node.modules ?? [] } : null,
    flags: mode === 'node' ? (trueFlags(raw.flags) ?? {}) : null,
  };
}

// ---------------------------------------------------------------- sources --

/** erp-core GET /v1/erp/settings (subset the shell needs). */
interface SettingsSector {
  sector?: string;
}

/** The business sector, or null when the settings read is refused (403, e.g. an unlicensed node). */
export async function fetchSector(): Promise<string | null> {
  try {
    const s = await erpApi.get<SettingsSector>('/settings');
    return typeof s?.sector === 'string' && s.sector ? s.sector : null;
  } catch (e) {
    if (isApiError(e) && e.status === 403) return null;
    throw e;
  }
}

/** ihjizli-profile GET /v1/business/memberships item. */
interface ProfileMembership {
  business_id?: string;
  role?: string;
  name_ar?: string;
}

/**
 * Cloud memberships from profile. Never fatal and never locks the screen: on
 * any failure the switcher simply shows the active business only.
 */
export async function fetchCloudMemberships(): Promise<Membership[]> {
  try {
    const res = await profileApi.get<{ memberships?: ProfileMembership[] }>('/v1/business/memberships', undefined, {
      businessId: null,
      authRecovery: false,
    });
    const out: Membership[] = [];
    for (const m of res?.memberships ?? []) {
      const role = parseRole(m.role);
      if (!m.business_id || !role) continue;
      out.push({ business_id: m.business_id, business_name: m.name_ar ?? '', role });
    }
    return out;
  } catch {
    return [];
  }
}

export const ME_QUERY_KEY = ['erp', 'me'] as const;

async function fetchRawMe(): Promise<MeResponse> {
  try {
    return await erpApi.get<MeResponse>('/me');
  } catch (e) {
    // A stored active business this person no longer belongs to: drop it and
    // let the server pick (owner membership, else the first).
    if (isApiError(e) && e.status === 403 && e.code === 'not_a_member' && sessionStore.getBusinessId()) {
      sessionStore.setBusinessId(null);
      sessionStore.setBranchId(null);
      return erpApi.get<MeResponse>('/me');
    }
    // A stored active branch that is gone (another business, removed): drop it
    // and take the server default (the main branch).
    if (isApiError(e) && e.status === 400 && e.code === 'invalid_branch' && sessionStore.getBranchId()) {
      sessionStore.setBranchId(null);
      return erpApi.get<MeResponse>('/me');
    }
    throw e;
  }
}

export async function fetchMe(): Promise<Me> {
  const raw = await fetchRawMe();
  const [sector, memberships] = await Promise.all([
    fetchSector(),
    raw.mode === 'node' ? Promise.resolve(sessionStore.getMemberships()) : fetchCloudMemberships(),
  ]);
  return normalizeMe(raw, { sector, memberships });
}

export function useMeQuery(enabled = true) {
  // A licence or role change on the server shows up when the person comes back
  // to the window (refetch on focus even inside staleTime), not only on reload.
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: 'always',
    // A locked session is not an outage: never retry it (the lock screen takes over).
    retry: (count, err) => !(isApiError(err) && err.status >= 400 && err.status < 500) && count < 1,
  });
}
